import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector } from "../../store/hooks";
import RenderChildren from "./RenderChildren";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import "./LanguageComponent.css";
import styled from "@emotion/styled";
import { LanguageComponent as LanguageComponent_ } from "./interfaces/base";
import { getPageLocaleUrl } from "./utilities/helpers";
import type { TranslationWebpage } from "./interfaces/translationWebpage";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import {
  isVisitorVisibleLanguage,
  languageCodeFromActiveLanguage,
  languageCodesEqual,
  normalizeLanguageCode,
  translationTargetUuid,
  translationTargetWebpageId,
  type LanguageLike,
} from "./utilities/languageCode";
import { resolveLanguageFlagIconUrl } from "./icons/languageFlagIcons";

function activeLanguageRowKey(lang: LanguageLike, index: number): string {
  const code = normalizeLanguageCode(languageCodeFromActiveLanguage(lang));
  if (code) return `${code}-${index}`;
  if (lang.language?.id != null) return `nl-${lang.language.id}-${index}`;
  if (lang.id != null) return `id-${lang.id}-${index}`;
  return `lang-${index}`;
}

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

export default function LanguageComponent({
  attributes,
  id,
  uuid,
  childElements,
  childWebpageComponentRelations,
}: any) {
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as LanguageComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "language-select",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as LanguageComponent_);

  const languageAttributes = component.attributes;

  const selectedWebpage = useAppSelector((state) => state.builder.pages.active);
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));

  const typographies = useAppSelector((state) => state.builder.typographies);
  const [style, setStyle] = useState<React.CSSProperties>();
  const [hoverStyle, setHoverStyle] = useState<any>();
  const colors = useAppSelector((state) => state.builder.colors);
  const webpages = useAppSelector((state) => state.builder.pages.list);
  const selectedWebpageData = useAppSelector((state) =>
    state.builder.pages.list.find((x) => x.uuid === selectedWebpage)
  );
  const languages = useAppSelector((state) => state.builder.languageSettings);

  const allLanguages = languages.activeLanguages;

  const primaryLanguages = languages.activeLanguages.filter((x) =>
    languageCodesEqual(
      languageCodeFromActiveLanguage(x),
      selectedWebpageData?.languageCode
    )
  );

  const otherLanguages = languages.activeLanguages.filter(
    (x) =>
      !languageCodesEqual(
        languageCodeFromActiveLanguage(x),
        selectedWebpageData?.languageCode
      )
  );

  const translationWebpageLanguages = otherLanguages
    .filter((x) =>
      selectedWebpageData?.translationWebpages?.some((y: TranslationWebpage) =>
        languageCodesEqual(
          y.languageCode,
          languageCodeFromActiveLanguage(x)
        )
      )
    )
    .filter((x) => isVisitorVisibleLanguage(x));

  const sortedLanguages = (() => {
    const merged = [...primaryLanguages, ...translationWebpageLanguages];
    const seen = new Set<string>();
    return merged.filter((lang, index) => {
      const key =
        normalizeLanguageCode(languageCodeFromActiveLanguage(lang)) ||
        activeLanguageRowKey(lang, index);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const params = useParams();
  let navigate = useNavigate();
  const pathname = useLocation();

  useEffect(() => {
    const compTypography = typographies?.find(
      (x: any) => x.id === languageAttributes.typography_id
    );
    if (compTypography) {
      setStyle({
        backgroundColor: attributes.styles.backgroundColor ?? "#fff",
        width: attributes.styles.width ?? "auto",

        fontSize: compTypography.fontSize + compTypography.fontSizeUnit,
        fontFamily: compTypography.fontFamily,
        fontStyle: compTypography.fontStyle,
        fontWeight: compTypography.fontWeight,
        letterSpacing: compTypography.letterSpacing,
        lineHeight: attributes.styles.lineHeight ?? compTypography.lineHeight,
        color: colors?.find((x: any) => x.id === compTypography.colorId)
          ?.color,
        transition:
          compTypography.transitionDuration +
          " color " +
          compTypography.transitionEffect,
      });
      setHoverStyle({
        "&:hover": {
          color:
            colors?.find(
              (x: any) => x.id === compTypography.linkHoverColorId
            )?.color + " !important",
        },
      });
    }
  }, [typographies, attributes, languageAttributes]);

  function handleLanguageSelect(lang) {
    const targetTranslationWebpage =
      selectedWebpageData?.translationWebpages?.find((tw: TranslationWebpage) =>
        languageCodesEqual(
          tw.languageCode,
          languageCodeFromActiveLanguage(lang)
        )
      );

    if (targetTranslationWebpage) {
      const targetUuid = translationTargetUuid(targetTranslationWebpage);
      const targetId = translationTargetWebpageId(targetTranslationWebpage);
      const targetWebpage =
        (targetId != null
          ? webpages.find((webpage) => webpage.id === targetId)
          : undefined) ??
        (targetUuid
          ? webpages.find(
              (webpage) => (webpage.uuid ?? "").trim() === targetUuid
            )
          : undefined);
      if (targetWebpage) {
        var to = getPageLocaleUrl(targetWebpage);
        if (to.substring(0, 1) !== "/") {
          to = "/" + to;
        }
        for (const key in params) {
          to = to.replace(`:${key}`, `${params[key]}`);
        }

        if (pathname.search.includes("?category=")) {
          to = to + pathname.search;
        }

        navigate(to);
      }
    }
  }

  const imageSizes = [
    {
      value: "small",
      width: "20px",
      height: "12px",
    },

    {
      value: "medium",
      width: "30px",
      height: "18px",
    },

    {
      value: "large",
      width: "45px",
      height: "24px",
    },
  ];

  return (
    <Div
      id={id.toString()}
      data-component-type="language-select"
      className={
        (attributes.classes ?? "") +
        " builder-component builder-component__language_select" +
        (matchesEditorTarget({ id, uuid }, editorId)
          ? " builder-component--active"
          : "")
      }
      style={{
        width: "fit-content",
        display: "inline-grid",
        minHeight: "0px !important",
      }}
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      <div
        style={{ display: "flex", alignItems: "center", marginTop: "-10px" }}
      >
        {languageAttributes.dropdown === "dropdown" && (
          <div>
            <div
              onClick={() => {
                setDropdownOpen(!dropdownOpen);
              }}
              className="language-dropdown-trigger"
              style={{
                color:
                  component.attributes?.styles?.color || style?.color || "#000",
              }}
            >
              <span
                style={{
                  ...style,
                  ...attributes.styles,
                  ...hoverStyle,
                  color:
                    component.attributes?.styles?.color ||
                    style?.color ||
                    "#000",
                  fontSize: languageAttributes.size,
                }}
              >
                {languageAttributes.type === "locale" &&
                  sortedLanguages[0] &&
                  languageCodeFromActiveLanguage(sortedLanguages[0])}
              </span>

              <span
                style={{
                  ...style,
                  ...attributes.styles,
                  ...hoverStyle,
                  color:
                    component.attributes?.styles?.color ||
                    style?.color ||
                    "#000",
                  fontSize: languageAttributes.size,
                }}
              >
                {languageAttributes.type === "name" &&
                  sortedLanguages[0] &&
                  languageCodeFromActiveLanguage(sortedLanguages[0])}
              </span>

              {languageAttributes.type === "image" && sortedLanguages[0] && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <img
                    src={resolveLanguageFlagIconUrl(
                      languageCodeFromActiveLanguage(sortedLanguages[0]),
                      sortedLanguages[0].language?.icon_url ?? ""
                    )}
                    style={{
                      width: languageAttributes.size,
                      objectFit: "cover",
                      height: languageAttributes.size
                        ? imageSizes.find(
                            (x) => x.value === languageAttributes.size
                          )?.height
                        : "12px",
                    }}
                    width={
                      languageAttributes.size !== "auto" &&
                      languageAttributes.size !== undefined &&
                      languageAttributes.size !== "px" &&
                      languageAttributes.size !== "rem" &&
                      languageAttributes.size !== ""
                        ? languageAttributes.size
                        : "24px"
                    }
                    height={"100%"}
                    className={""}
                  ></img>

                  {/* {sortedLanguages.map((lang) => {
                    return (
                      <img
                        src={lang.language.icon_url}
                        style={{
                          width: languageAttributes.size,
                          objectFit: "cover",
                          height: languageAttributes.size
                            ? imageSizes.find(
                                (x) => x.value === languageAttributes.size
                              )?.height
                            : "12px",
                        }}
                        width={
                          languageAttributes.size !== "auto" &&
                          languageAttributes.size !== undefined &&
                          languageAttributes.size !== "px" &&
                          languageAttributes.size !== "rem" &&
                          languageAttributes.size !== ""
                            ? languageAttributes.size
                            : "24px"
                        }
                        height={"100%"}
                        className={""}
                      ></img>
                    );
                  })} */}
                </div>
              )}

              <ArrowDropDownIcon
                style={{
                  fontSize: languageAttributes.size,
                }}
              />
            </div>

            {dropdownOpen && (
              <div
                className="language-dropdown"
                style={{
                  position: "absolute",
                  padding:
                    languageAttributes.type === "image" ? "10px" : "5px 10px",
                }}
              >
                {languageAttributes.type === "locale" && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {sortedLanguages?.slice(1).map((lang, index) => {
                      return (
                        <span
                          onClick={() => handleLanguageSelect(lang)}
                          key={activeLanguageRowKey(lang, index)}
                          className=""
                          style={{
                            fontWeight: "400",
                            textTransform: "uppercase",
                            color: "#333",
                            cursor: "pointer",
                            fontSize: "15px",
                          }}
                        >
                          {languageCodeFromActiveLanguage(lang)}
                        </span>
                      );
                    })}
                  </div>
                )}
                {languageAttributes.type === "name" && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {sortedLanguages?.slice(1).map((lang, index) => {
                      return (
                        <span
                          onClick={() => handleLanguageSelect(lang)}
                          key={activeLanguageRowKey(lang, index)}
                          className=""
                          style={{
                            fontWeight: "400",
                            textTransform: "uppercase",
                            color: "#333",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          {languageCodeFromActiveLanguage(lang)}
                        </span>
                      );
                    })}
                  </div>
                )}

                {languageAttributes.type === "image" && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    {sortedLanguages?.slice(1).map((lang, index) => {
                      return (
                        <img
                          key={activeLanguageRowKey(lang, index)}
                          onClick={() => handleLanguageSelect(lang)}
                          src={resolveLanguageFlagIconUrl(
                            languageCodeFromActiveLanguage(lang),
                            lang.language?.icon_url ?? ""
                          )}
                          style={{
                            objectFit: "cover",

                            cursor: "pointer",
                            height: "100%",
                            margin: "0",
                            padding: "0",
                          }}
                          width={"20px"}
                          className={""}
                        ></img>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            height: "100%",
          }}
        >
          {languageAttributes.type === "locale" &&
            languageAttributes.dropdown === "none" &&
            sortedLanguages?.map((lang, index) => {
              return (
                <div
                  onClick={() => handleLanguageSelect(lang)}
                  key={activeLanguageRowKey(lang, index)}
                  className=""
                  style={{
                    ...style,
                    ...attributes.styles,
                    ...hoverStyle,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color:
                      component.attributes?.styles?.color ||
                      style?.color ||
                      "#000",
                    fontSize: languageAttributes.size,
                    marginTop: "4px",
                  }}
                >
                  {languageCodeFromActiveLanguage(lang)}
                </div>
              );
            })}
          {languageAttributes.type === "name" &&
            languageAttributes.dropdown === "none" &&
            sortedLanguages?.map((lang, index) => {
              return (
                <div
                  onClick={() => handleLanguageSelect(lang)}
                  key={activeLanguageRowKey(lang, index)}
                  className=""
                  style={{
                    ...style,
                    ...attributes.styles,
                    ...hoverStyle,

                    textTransform: "uppercase",
                    color:
                      component.attributes?.styles?.color ||
                      style?.color ||
                      "#000",
                    fontSize: languageAttributes.size,
                    marginTop: "4px",
                  }}
                >
                  {languageCodeFromActiveLanguage(lang)}
                </div>
              );
            })}
          {languageAttributes.type === "image" &&
            languageAttributes.dropdown === "none" &&
            sortedLanguages?.map((lang, index) => {
              return (
                <div
                  key={activeLanguageRowKey(lang, index)}
                  className=""
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "inherit",
                    width: languageAttributes.size,
                  }}
                  onClick={() => {
                    handleLanguageSelect(lang);
                  }}
                >
                  <img
                    src={resolveLanguageFlagIconUrl(
                      languageCodeFromActiveLanguage(lang),
                      lang.language?.icon_url ?? ""
                    )}
                    style={{
                      width: attributes.size,

                      objectFit: "cover",
                      height: languageAttributes.size
                        ? imageSizes.find(
                            (x) => x.value === languageAttributes.size
                          )?.height
                        : "12px",
                    }}
                    width={
                      languageAttributes.size !== "auto" &&
                      languageAttributes.size !== undefined &&
                      languageAttributes.size !== "px"
                        ? languageAttributes.size
                        : "24px"
                    }
                    height={"100%"}
                    className={""}
                  ></img>
                </div>
              );
            })}
        </div>
      </div>
    </Div>
  );
}
